import { ExecutionCardStyles as styles } from './execution-card.styles';
import { ExecutionCardTemplate as template, EXECUTION_TABS } from './execution-card.template';
import { attr, customElement, FASTElement, observable } from '@microsoft/fast-element';
import { IArteriaNegotiation, INegotiation } from '../../../types';
import { logger, Message, SocketSubscription } from '@genesislcap/foundation-comms';
import { IArteriaNegotiationService } from '../../../services/arteria_negotiation.service';
import { updateArray } from '../../../utils/dataserver';
import {
  CONTRACT_TYPE,
  NEGOTIATION_STATUS,
  PROFILE_RIGHTS,
  ProfileVisibility,
} from '../../../utils';
import { Tabs } from '@genesislcap/foundation-zero';
import { INegotiationService } from '../../../services/negotiation.service';
import { formatNumberToDate, isToday, isYesterday } from '../../../utils/formatting';
import { notifyError } from '../../../utils/notify';
import { TabButtons } from '../../common/tab-buttons/tab-buttons';
import {
  EXECUTION_NOTIFICATION_TYPE,
  IExecutionNotification,
  IExecutionService,
} from '../../../services/execution.service';
import { IUserService } from '../../../services/user.service';
import { ICompanyService } from '../../../services/company.service';

const name = 'execution-card';

@customElement({
  name,
  template,
  styles,
})
export class ExecutionCard extends FASTElement {
  @IUserService userService: IUserService;
  @IArteriaNegotiationService arteriaNegotiationService: IArteriaNegotiationService;
  @INegotiationService negotiationService: INegotiationService;
  @IExecutionService executionService: IExecutionService;
  @ICompanyService companyService: ICompanyService;

  @attr executionNotifications: IExecutionNotification[];
  @observable negotiations: IArteriaNegotiation[];
  @attr @observable selectedNegotiation: IArteriaNegotiation;
  @observable autoExecutedNegotiations: INegotiation[];
  @observable negotiationCounter: number = 0;
  @observable expiredNegotiationCounter: number = 0;
  @observable autoExecutedNegotiationsCounter: number = 0;
  @observable executionPageTabs: Tabs;
  @observable autoExecuteTabElement: TabButtons;
  @attr @observable activeTabId: string;
  @observable previousDate: string;
  @observable uniqueDates: string[];
  @observable backToNegotiationHeader: string = '';
  @observable backToNegotiationDescription: string =
    'If you choose to proceed, signatures will be removed and the documents will be re-opened for negotiations.';
  @observable showBackToNegotiatonConfirm: boolean = false;
  private negotiationSubscription: SocketSubscription;
  private arteriaNegotiationSubscription: SocketSubscription;

  private selectedRfqTab: EXECUTION_TABS = EXECUTION_TABS.AWAITING_KEY_METRICS;

  connectedCallback() {
    super.connectedCallback();
    this.streamArteriaNegotiations();
    this.getNegotiations();
    this.getDate();
  }

  private streamArteriaNegotiations() {
    let resultList = [];
    this.arteriaNegotiationSubscription?.unsubscribe();

    let companyCriteria = '';
    if (
      this.userService.hasBuyerAndSellerVisibility() &&
      this.userService.getCurrentSide() === ProfileVisibility.SELLER
    ) {
      companyCriteria = `SELLER_COMPANY_ID == '${
        this.companyService.getCurrentCompany().CASCADE_COMPANY_ID
      }'`;
    } else if (
      this.userService.hasBuyerAndSellerVisibility() &&
      this.userService.getCurrentSide() === ProfileVisibility.BUYER
    ) {
      companyCriteria = `BUYER_COMPANY_ID == '${
        this.companyService.getCurrentCompany().CASCADE_COMPANY_ID
      }'`;
    }

    const criteria = `${
      companyCriteria ? companyCriteria + ' && ' : ''
    } IS_VISIBLE_TO_EXECUTION == true && NEGOTIATION_STATUS != 'TRADED_AWAY'`;
    this.arteriaNegotiationSubscription = this.arteriaNegotiationService
      .getArteriaNegotiations(criteria)
      .subscribe({
        next: (streamMessage: Message) => {
          this.updateShouldShowTimeBar();

          resultList = streamMessage.ROW ?? null;

          if (!resultList) {
            this.negotiations = [];
            const resultError = streamMessage.DETAILS?.ERROR;
            logger.error(resultError);
            return;
          }

          this.negotiations = updateArray(resultList, this.negotiations).sort(
            (a, b) => b.MODIFIED_AT - a.MODIFIED_AT
          );

          if (!this.negotiations) {
            this.selectedNegotiation = undefined;
            return;
          }

          if (this.selectedRfqTab == EXECUTION_TABS.AWAITING_KEY_METRICS) {
            const filteredNegotiations = this.negotiations.filter(
              (negotiation) =>
                ![
                  'EXPIRED',
                  'REJECTED',
                  'FULL_DELETED',
                  'SELLER_DELETED',
                  'BUYER_DELETED',
                  'IN_PROGRESS_ARTERIA',
                  'READY_FOR_TRADE_EXECUTION',
                  'REVIEW_EXECUTION_CHECKLIST',
                ].includes(negotiation.NEGOTIATION_STATUS)
            );

            this.updateSelectedNegotiation(filteredNegotiations);
          } else if (this.selectedRfqTab == EXECUTION_TABS.EXPIRED) {
            const filteredNegotiations = this.negotiations.filter(
              (negotiation) => negotiation.NEGOTIATION_STATUS === NEGOTIATION_STATUS.EXPIRED
            );

            this.updateSelectedNegotiation(filteredNegotiations);
          }

          this.updateShouldShowTimeBar();
        },
        error: (error: any) => {
          this.negotiations = [];
          logger.error(error);
        },
      });
  }

  executionNotificationsChanged(prev: IExecutionNotification[], next: IExecutionNotification[]) {
    this.autoExecutedNegotiationsCounter = next.filter(
      (x) => x.EXECUTION_NOTIFICATION_TYPE === EXECUTION_NOTIFICATION_TYPE.AUTO_EXECUTE
    ).length;
    this.negotiationCounter = next.filter(
      (x) =>
        x.EXECUTION_NOTIFICATION_TYPE === EXECUTION_NOTIFICATION_TYPE.RFQ &&
        x.NEGOTIATION_STATUS !== NEGOTIATION_STATUS.EXPIRED
    ).length;

    this.expiredNegotiationCounter = next.filter(
      (x) =>
        x.EXECUTION_NOTIFICATION_TYPE === EXECUTION_NOTIFICATION_TYPE.RFQ &&
        x.NEGOTIATION_STATUS === NEGOTIATION_STATUS.EXPIRED
    ).length;
  }

  private updateSelectedNegotiation(filteredNegotiations: Array<IArteriaNegotiation>) {
    this.selectedNegotiation = filteredNegotiations.find(
      (negotiation) => negotiation.NEGOTIATION_ID === this.selectedNegotiation?.NEGOTIATION_ID
    );

    if (!this.selectedNegotiation) {
      if (filteredNegotiations[0]) {
        this.selectedNegotiation = filteredNegotiations[0];
        return;
      }
      this.selectedNegotiation = undefined;
    }
  }
  private updateShouldShowTimeBar() {
    const needActionTransaction = this.negotiations?.filter((negotiation) =>
      [
        'PENDING_EXECUTION_TIMER',
        'PENDING_EXECUTION',
        'EXECUTED_AWAITING_BUYER',
        'EXECUTED_AWAITING_SELLER',
      ].includes(negotiation.NEGOTIATION_STATUS)
    );
    this.$emit('updateShouldShowTimeBar', needActionTransaction?.length > 0);
  }

  private getNegotiations() {
    let resultList = [];
    this.negotiationSubscription?.unsubscribe();

    let companyCriteria = '';
    if (
      this.userService.hasBuyerAndSellerVisibility() &&
      this.userService.getCurrentSide() === ProfileVisibility.SELLER
    ) {
      companyCriteria = `SELLER_COMPANY_ID == '${
        this.companyService.getCurrentCompany().CASCADE_COMPANY_ID
      }' && `;
    } else if (
      this.userService.hasBuyerAndSellerVisibility() &&
      this.userService.getCurrentSide() === ProfileVisibility.BUYER
    ) {
      companyCriteria = `BUYER_COMPANY_ID == '${
        this.companyService.getCurrentCompany().CASCADE_COMPANY_ID
      }' && `;
    }

    const criteria = `${companyCriteria} VPPA_CONTRACT_TYPE === '${CONTRACT_TYPE.MINI_POST_BUILD_VPPA_AUTO_EXECUTED}'`;
    this.negotiationSubscription = this.negotiationService
      .getAllNegotiations({
        CRITERIA_MATCH: criteria,
      })
      .subscribe({
        next: (streamMessage: Message) => {
          resultList = streamMessage.ROW ?? null;
          if (resultList) {
            this.autoExecutedNegotiations = updateArray(resultList, this.autoExecutedNegotiations);

            this.uniqueDates = [
              ...new Set(
                this.autoExecutedNegotiations.map((neg) => formatNumberToDate(neg.CREATED_AT))
              ),
            ];

            this.setSelectedNegotiationById(resultList);
          }
        },
        error: (error: any) => {
          this.autoExecutedNegotiations = [];
          logger.error(error);
        },
      });
  }

  public readableStatus(status) {
    switch (status) {
      case 'PENDING_EXECUTION':
        return 'Confirm Now';
      case 'PENDING_EXECUTION_TIMER':
        return 'Confirm Now';
      case 'EXECUTED_AWAITING_BUYER':
        return 'Pending Buyer';
      case 'EXECUTED_AWAITING_SELLER':
        return 'Pending Seller';
      case 'EXECUTED':
        return 'Successful';
      default:
        return '';
    }
  }

  public statusClass(status) {
    switch (status) {
      case 'PENDING_EXECUTION':
        return 'red';
      case 'PENDING_EXECUTION_TIMER':
        return 'red';
      case 'EXECUTED_AWAITING_BUYER':
      case 'EXECUTED_AWAITING_SELLER':
        return 'yellow';
      case 'EXECUTED':
        return 'green';
      default:
        return '';
    }
  }

  public deleteNegotiation(negotiationID: string) {
    this.negotiationService.eventDeleteExpiredNegotiation(negotiationID).then(
      (response) => {
        if (response && response.MESSAGE_TYPE === 'EVENT_ACK') {
        } else {
          notifyError(response.ERROR[0].TEXT);
          logger.error(response.ERROR);
        }
      },
      (error) => {
        notifyError(error);
        logger.error(error);
      }
    );
  }

  public getCompany(negotiation: IArteriaNegotiation) {
    if (
      this.companyService.getCurrentCompany().CASCADE_COMPANY_ID === negotiation.BUYER_COMPANY_ID
    ) {
      return negotiation.SELLER_COMPANY_NAME?.toUpperCase();
    } else {
      return negotiation.BUYER_COMPANY_NAME?.toUpperCase();
    }
  }

  public rfqsTab() {
    this.$emit('tabSelected', true);
    this.rfqTabSelected(this.selectedRfqTab);
  }

  public autoExecuteTab() {
    this.$emit('tabSelected', false);
    if (this.autoExecutedNegotiations) {
      const moreRecentNegotiation = this.autoExecutedNegotiations.reduce((biggestDateObj, obj) => {
        return obj.CREATED_AT > biggestDateObj?.CREATED_AT ? obj : biggestDateObj;
      });
      this.setSelectedNegotiation(moreRecentNegotiation);
    } else {
      this.setSelectedNegotiation(null);
    }
  }

  private getDate() {
    const formattedDate = new Date(new Date().setDate(new Date().getDate() - 2));
    const day = formattedDate.getDate();
    const month = Number(formattedDate.getMonth()) + 1;
    const year = formattedDate.getFullYear();
    this.previousDate = `${month}/${day}/${year}`;
  }

  public getHeader(date: Date | number) {
    if (isToday(date)) {
      return 'Today';
    } else if (isYesterday(date)) {
      return 'Yesterday';
    } else {
      return formatNumberToDate(date);
    }
  }

  openBackToNegotiationConfirm() {
    this.backToNegotiationHeader = `Execution will be halted and ${this.selectedNegotiation.TICKER}-${this.selectedNegotiation.VPPA_UNIQUE_ID} will return to Negotiations`;
    this.showBackToNegotiatonConfirm = true;
  }

  public closeBackToNegotiationConfirm(isConfirmation) {
    if (isConfirmation) {
      this.negotiationService.eventArteriaReopen(this.selectedNegotiation.NEGOTIATION_ID).then(
        (response) => {
          if (response && response.MESSAGE_TYPE === 'EVENT_ACK') {
            // TODO: Let the user know its been updated
            this.showBackToNegotiatonConfirm = false;
          } else {
            // TODO: Use a Toast to let the user know that something went wrong
            logger.error(response.ERROR);
          }
        },
        (error) => {
          // TODO: Use a Toast to let the user know that something went wrong
          logger.error(error);
        }
      );
    } else {
      this.showBackToNegotiatonConfirm = false;
    }
  }

  public setSelectedNegotiation(negotiation) {
    this.selectedNegotiation = negotiation;
    if (
      negotiation &&
      this.executionNotifications?.find((elm) => elm.NEGOTIATION_ID === negotiation.NEGOTIATION_ID)
    ) {
      this.executionService
        .deleteExecutionNotification(negotiation.NEGOTIATION_ID)
        .then((response) => {
          if (response?.MESSAGE_TYPE === 'EVENT_NACK') {
            logger.error(response);
          }
        })
        .catch((error) => {
          logger.error(error);
        });
    }
  }

  public setSelectedNegotiationById(negotiations: INegotiation[]) {
    const negotiationId = this.executionService.getNegotiationId();
    if (negotiationId != null) {
      for (const negotiation of negotiations) {
        if (negotiation.NEGOTIATION_ID === negotiationId) {
          this.autoExecuteTabElement.click();
          this.setSelectedNegotiation(negotiation);
          this.executionService.setNegotiationId(null);
        }
      }
    }
  }

  public rfqTabSelected(tab: EXECUTION_TABS) {
    this.selectedRfqTab = tab;
    if (this.selectedRfqTab === EXECUTION_TABS.AWAITING_KEY_METRICS) {
      const filteredNegotiations = this.negotiations.filter(
        (negotiation) =>
          ![
            'EXPIRED',
            'REJECTED',
            'FULL_DELETED',
            'SELLER_DELETED',
            'BUYER_DELETED',
            'IN_PROGRESS_ARTERIA',
            'READY_FOR_TRADE_EXECUTION',
            'REVIEW_EXECUTION_CHECKLIST',
          ].includes(negotiation.NEGOTIATION_STATUS)
      );
      this.updateSelectedNegotiation(filteredNegotiations);
    } else if (this.selectedRfqTab === EXECUTION_TABS.EXPIRED) {
      const filteredNegotiations = this.negotiations?.filter(
        (negotiation) => negotiation.NEGOTIATION_STATUS === NEGOTIATION_STATUS.EXPIRED
      );
      this.updateSelectedNegotiation(filteredNegotiations);
    }
  }

  public isSellerCompany(negotiation: INegotiation): boolean {
    if (
      negotiation.SELLER_COMPANY_ID === this.companyService.getCurrentCompany().CASCADE_COMPANY_ID
    ) {
      return true;
    }
    return false;
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.negotiationSubscription.unsubscribe();
    this.arteriaNegotiationSubscription.unsubscribe();
  }
}
